import { MutableRefObject, useEffect } from 'react';

import { Map } from 'mapbox-gl';

import { debounce } from 'lodash';

const MAP_RESIZE_ANIMATION_TIME = 0; // doesn't seem to be an issue

export function useMapResize(
  map: Map | null,
  mapContainerRef: MutableRefObject<HTMLDivElement | null>,
) {
  useEffect(() => {
    if (!map || !mapContainerRef.current) return undefined;
    const runResize = () => {
      if (!map) return;

      if (map.isMoving() || map.isEasing() || map.isZooming()) {
        debouncedResize();
        return;
      }

      try {
        const center = map.getCenter();
        map.resize();
        map.setCenter(center);
      } catch (e) {
        console.error('map resizing halted', e);
      }
    };

    const debouncedResize = debounce(runResize, MAP_RESIZE_ANIMATION_TIME);

    const onLoad = () => debouncedResize();

    // Ensure we don’t attempt a resize before the map is ready
    if (!map.loaded()) {
      map.once('load', onLoad);
    }

    const resizeObserver = new ResizeObserver(() => debouncedResize());
    resizeObserver.observe(mapContainerRef.current);

    return () => {
      resizeObserver.disconnect();
      debouncedResize.cancel();
      map.off('load', onLoad);
    };
  }, [map, mapContainerRef]);
}
